import { useClerk } from '@clerk/clerk-react'
import { useAppTheme } from '../hooks/useAppTheme'

/**
 * Shown to a signed-in identity that Clerk authenticated but CannaSpy has not
 * provisioned (the backend returns 403 ACCESS_PENDING). The global denial
 * handler routes here; this page is the terminal denial state.
 *
 * Deliberately self-contained:
 *   • makes ZERO CannaSpy API calls (so it can never re-trigger the denial that
 *     sent the user here, and never loops),
 *   • renders entirely from the denial state — only static, truthful copy,
 *   • is mounted OUTSIDE the data-fetching Layout (see App.tsx),
 *   • offers a safe Clerk sign-out that returns to /sign-in,
 *   • makes NO billing, trial, activation-time, or automatic-access promise,
 *   • omits a support link — no verified support destination exists yet.
 *
 * Only Clerk session state (sign-out) is touched; nothing is fetched.
 */
export default function AccessPending() {
  const { signOut } = useClerk()
  // Rendered outside Layout, so ensure the data-theme attribute (and thus the
  // CSS design tokens) are applied — light/dark follow the user's saved choice,
  // defaulting to light. Pure DOM/localStorage; makes no API calls.
  useAppTheme()

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
      }}
    >
      <div style={{ width: '100%', maxWidth: 520 }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28, justifyContent: 'center' }}>
          <div
            style={{
              width: 40,
              height: 40,
              background: 'linear-gradient(135deg,#09A1A1 0%,#F6C992 100%)',
              borderRadius: 11,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 14px rgba(9,161,161,0.45)',
            }}
          >
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2}>
              <polygon points="12 2 22 8.5 22 15.5 12 22 2 15.5 2 8.5" />
              <line x1="12" y1="2" x2="12" y2="22" />
              <line x1="2" y1="8.5" x2="22" y2="8.5" />
            </svg>
          </div>
          <div style={{ fontFamily: 'var(--display)', fontSize: 24, fontWeight: 400, color: 'var(--text-1)', letterSpacing: '-0.015em' }}>
            CannaSpy
          </div>
        </div>

        {/* Status card */}
        <div
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--border-2)',
            borderRadius: 'var(--r)',
            padding: '28px 32px',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 14,
              background: 'var(--surface-2)',
              border: '1px solid var(--border-2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}
          >
            <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth={1.8}>
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>

          <h1
            data-testid="access-pending-title"
            style={{
              fontFamily: 'var(--display)',
              fontSize: 28,
              fontWeight: 400,
              color: 'var(--text-1)',
              letterSpacing: '-0.02em',
              marginBottom: 10,
              lineHeight: 1.1,
            }}
          >
            Your access is pending
          </h1>

          <p
            data-testid="access-pending-message"
            style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.65, maxWidth: 420, margin: '0 auto 14px' }}
          >
            CannaSpy is invitation-only during our pilot. You're signed in, but this account
            isn't provisioned for the platform yet. If you believe you should have access,
            reach out to your CannaSpy representative to be added.
          </p>

          <p style={{ fontSize: 12, color: 'var(--text-3)', lineHeight: 1.6, maxWidth: 420, margin: '0 auto 22px' }}>
            You can sign out and return once your workspace has been set up.
          </p>

          <button
            data-testid="access-pending-signout"
            onClick={() => { void signOut({ redirectUrl: '/sign-in' }) }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '10px 20px',
              borderRadius: 'var(--r-sm)',
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: 'var(--sans)',
              border: '1.5px solid var(--border-2)',
              background: 'var(--surface-3)',
              color: 'var(--text-1)',
            }}
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  )
}
